import * as React from 'react';

import { cn } from '@/lib/utils';

import {
  FLEX_ALIGN as FLEX_ALIGN_SCALE,
  GAP_SCALE,
  GRID_AUTO_ROWS,
  GRID_BASE_COLUMNS,
  GRID_RESPONSIVE_COLUMNS,
} from './tokens';

interface GridProps extends React.HTMLAttributes<HTMLElement> {
  as?: React.ElementType;
  cols?: number;
  sm?: number;
  md?: number;
  lg?: number;
  xl?: number;
  gap?: keyof typeof GAP_SCALE;
  align?: keyof typeof FLEX_ALIGN_SCALE;
  autoRows?: keyof typeof GRID_AUTO_ROWS;
  children?: React.ReactNode;
}

const Grid = React.forwardRef<HTMLElement, GridProps>(
  (
    {
      as: Component = 'div',
      cols = 1,
      sm,
      md,
      lg,
      xl,
      gap = 'md',
      align = 'stretch',
      autoRows = 'none',
      className,
      children,
      ...props
    },
    ref
  ) => {
    const classes = [
      'grid',
      GAP_SCALE[gap],
      FLEX_ALIGN_SCALE[align],
      GRID_BASE_COLUMNS[cols as keyof typeof GRID_BASE_COLUMNS],
      GRID_AUTO_ROWS[autoRows],
    ];

    // Add responsive column classes
    const responsiveBreakpoints = [
      { breakpoint: 'sm', value: sm },
      { breakpoint: 'md', value: md },
      { breakpoint: 'lg', value: lg },
      { breakpoint: 'xl', value: xl },
    ] as const;

    for (const { breakpoint, value } of responsiveBreakpoints) {
      if (
        value &&
        GRID_RESPONSIVE_COLUMNS[breakpoint][
          value as keyof (typeof GRID_RESPONSIVE_COLUMNS)[typeof breakpoint]
        ]
      ) {
        classes.push(
          GRID_RESPONSIVE_COLUMNS[breakpoint][
            value as keyof (typeof GRID_RESPONSIVE_COLUMNS)[typeof breakpoint]
          ]
        );
      }
    }

    return (
      <Component ref={ref} className={cn(classes, className)} {...props}>
        {children}
      </Component>
    );
  }
);
Grid.displayName = 'Grid';

export { Grid };
